import { TextField, Typography, Grid } from "@mui/material";
import ProductSelector, { SelectedProductDetails } from "domains/products/components/product-selector/ProductSelector";
import { useCallback, useState } from "react";
import localisations from "../../../localisation";
import allocationLocalisations from "domains/allocations/localisation";
import { ProductQuantityStatus } from "domains/allocations/components/product-quantity-status";
import { ProductWithInPartitionStatus } from "domains/allocations/components/product-within-partition-status";
import { SelectedPartitionDetails } from "domains/partitions/components/partition-selector/PartitionSelector";
import { QuerySnapshotResponse } from "domains/allocations/services/allocation-service";

type SelectProductAndQuantityProps = {
    fieldCompanyId: number;
    fields: {
        sourcePartitionDetails: SelectedPartitionDetails,
        productDetails?: SelectedProductDetails;
        quantityChange: number;
    };
    handleTransferProductStatus: (status: boolean) => void;
    handleProductQuantityStatus: (status: boolean) => void;
    handleFieldChange: (fields: any) => void;
};

export default function SelectProductAndQuantityStage(props: SelectProductAndQuantityProps) {
    const {
        heading,
        blurb,
        quantityLabel,
        quantityHelperText
    } = localisations.movementAllocation.stages.selectProductAndQuantity;
    const { fields, handleFieldChange } = props;
    const [quantity, setQuantity] = useState(fields.quantityChange ?? 0);
    const [productWithInPartitionRefresh, setProductWithInPartitionRefresh] = useState(false);

    const onProductQuantityStatusLoaded = useCallback((status) => {
        props.handleProductQuantityStatus(status);
    }, [props]);

    const onProductWithInPartitionStatusLoaded = useCallback((status) => {
        setProductWithInPartitionRefresh(false);
        props.handleTransferProductStatus(status);
    }, [props]);

    const onProductSelected = useCallback((productDetails) => {
        setProductWithInPartitionRefresh(true);
        handleFieldChange({ productDetails: productDetails })
    }, [handleFieldChange]);

    const onProductQuantityChanged = useCallback((event) => {
        let quantityChange = parseInt(event.target.value);
        setQuantity(quantityChange);
        handleFieldChange({ quantityChange });
    }, [handleFieldChange]);

    const validateStock = (data: QuerySnapshotResponse): boolean => {
        let item = data.items.find(i => i.productId === fields.productDetails?.id);
        return (fields.quantityChange == null || item === undefined || item.quantity == null)
            ? false
            : fields.quantityChange !== 0 && item.quantity >= fields.quantityChange;
    }

    const formatMessageQuantity = (status: boolean): string =>
        status ? allocationLocalisations.transferStock.sufficientQuantityStatus : allocationLocalisations.transferStock.insufficientQuantityStatus;

    return (
        <>
            <Typography mb={4} variant="h5" gutterBottom>
                {heading}
            </Typography>

            <Typography variant="body1" gutterBottom>
                {blurb}
            </Typography>

            <Grid container rowSpacing={8}>
                <Grid item xs={12}>
                    <ProductSelector
                        fieldCompanyId={props.fieldCompanyId}
                        initialProductId={fields.productDetails?.id}
                        onSelected={onProductSelected}
                    />

                    <ProductWithInPartitionStatus
                        handleTransferProductStatus={props.handleTransferProductStatus}
                        productId={fields.productDetails?.id ?? null}
                        partitionId={fields.sourcePartitionDetails?.id}
                        onLoaded={onProductWithInPartitionStatusLoaded}
                        forceRefresh={productWithInPartitionRefresh}
                    />
                </Grid>
                <Grid item xs={12}>
                <TextField
                        id="quantity-field"
                        label={quantityLabel}
                        placeholder="Quantity"
                        helperText={quantityHelperText}
                        fullWidth
                        value={quantity}
                        type="number"
                        InputProps={{
                            inputProps: { min: 0 }
                        }}
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                            onProductQuantityChanged(event)
                        }}
                    />
                    <ProductQuantityStatus
                        validStock={validateStock}
                        productId={fields.productDetails?.id ?? null}
                        partitionId={fields.sourcePartitionDetails?.id}
                        onLoaded={onProductQuantityStatusLoaded}
                        quantity={fields.quantityChange}
                        formatMessage={formatMessageQuantity}
                    />
                </Grid>
            </Grid>
        </>
    );
}